import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import { supabase } from '../lib/supabase';
import { SEO } from '../shared/components/SEO';
import type { EmailNotificationType } from '../services/emailNotificationPreferences';

const EMAIL_TYPE_LABELS: Record<string, string> = {
  career_tips: 'weekly career tip emails',
  weekly_digest: 'weekly digest emails',
  job_digest: 'job recommendation emails',
  optimization_reminder: 'optimization reminder emails',
  onboarding_nudge: 'onboarding reminder emails',
  blog: 'blog newsletter emails',
};

const VALID_TYPES: EmailNotificationType[] = [
  'career_tips',
  'weekly_digest',
  'job_digest',
  'optimization_reminder',
  'onboarding_nudge',
  'blog',
];

const ERROR_MESSAGES: Record<string, string> = {
  missing_token: 'This unsubscribe link is missing a verification token. Please use the link directly from your email.',
  expired: 'This unsubscribe link has expired. Please use the link from a more recent email, or update your preferences in Account Settings.',
  invalid: 'This unsubscribe link is not valid. Please use the link directly from your email.',
  unavailable: 'The unsubscribe service is temporarily unavailable. Please try again later.',
  failed: 'We could not save your preference right now. Please try again later.',
};

function readSearchParams(): URLSearchParams {
  if (typeof window === 'undefined') return new URLSearchParams();
  return new URLSearchParams(window.location.search);
}

const EmailUnsubscribePage: React.FC = () => {
  const params = useMemo(() => readSearchParams(), []);
  const status = params.get('status') === 'success' ? 'success' : 'error';
  const emailType = params.get('type') ?? '';
  const reason = params.get('reason') ?? 'invalid';
  const [redirecting, setRedirecting] = useState(true);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;

      if (session && status === 'success') {
        const highlight = VALID_TYPES.includes(emailType as EmailNotificationType)
          ? emailType
          : '';
        const qs = new URLSearchParams({ tab: 'notifications' });
        if (highlight) qs.set('highlight', highlight);
        window.location.replace(`/account-settings?${qs.toString()}`);
        return;
      }

      if (session && status === 'error') {
        const qs = new URLSearchParams({ tab: 'notifications', unsub_error: reason });
        window.location.replace(`/account-settings?${qs.toString()}`);
        return;
      }

      setRedirecting(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [status, emailType, reason]);

  const success = status === 'success';
  const label = EMAIL_TYPE_LABELS[emailType] ?? 'these emails';
  const title = success ? 'Successfully unsubscribed' : 'Unsubscribe failed';
  const message = success
    ? `You will no longer receive ${label} from GoGrabJob. Sign in to manage all notification preferences in Account Settings → Notifications.`
    : ERROR_MESSAGES[reason] ?? ERROR_MESSAGES.invalid;

  if (redirecting) {
    return (
      <>
        <SEO
          title="Updating preferences | GoGrabJob"
          description="Redirecting to your notification settings."
          canonical="https://www.gograbjob.com/email/unsubscribe"
          noIndex
        />
        <div className="min-h-screen bg-[#f5f0e8] flex flex-col items-center justify-center gap-3 px-4">
          <Loader2 className="h-8 w-8 animate-spin text-[#15803d]" aria-hidden />
          <p className="text-sm text-[#57534e]">Taking you to notification settings…</p>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title={`${title} | GoGrabJob`}
        description={message}
        canonical="https://www.gograbjob.com/email/unsubscribe"
        noIndex
      />
      <div className="min-h-screen bg-[#f5f0e8] flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md rounded-xl border border-[#e8e0d4] bg-[#fafaf9] px-8 py-10 text-center shadow-sm">
          <div className="mb-4 flex justify-center">
            {success ? (
              <CheckCircle className="h-10 w-10 text-[#15803d]" aria-hidden />
            ) : (
              <XCircle className="h-10 w-10 text-[#dc2626]" aria-hidden />
            )}
          </div>
          <h1 className="mb-3 text-xl font-semibold text-[#1c1917]">{title}</h1>
          <p className="mb-6 text-[0.9375rem] leading-relaxed text-[#57534e]">{message}</p>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            <a
              href="/login"
              className="inline-block rounded-lg border border-[#15803d] px-5 py-2.5 text-sm font-semibold text-[#15803d] no-underline hover:bg-[#15803d]/5"
            >
              Sign in
            </a>
            <a
              href="/"
              className="inline-block rounded-lg bg-[#5F8568] px-5 py-2.5 text-sm font-semibold text-white no-underline hover:bg-[#547a5d]"
            >
              Back to GoGrabJob
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmailUnsubscribePage;
